import { Inject, inject, Injectable, Optional } from '@angular/core';
import { ActionButton, ActionButtonConfigService } from 'gung-common';
import { Customer } from '../models';
import { Observable, filter, first, forkJoin, of, switchMap } from 'rxjs';
import { GungModalService } from './gung-modal/gung-modal.service';
import { AuthService } from './auth/auth.service';
import { Router } from '@angular/router';
import { SelectedCustomerService } from './selected-customer/selected-customer.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NavbarConfigService } from './navbar-config/navbar-config.service';
import { CreateUserModalComponent } from '../components/users/create-user-modal/create-user-modal.component';
import { ReportCentralReportService } from '../modules/report-central/services/report-central-report.service';
import { FeatureActivatedGuardWrapper } from '../guards/feature-activated/feature-activated.guard';
import { NavigationConfig } from '../models/navigation-config';


@Injectable({
  providedIn: 'root'
})
export class CustomerActionConfigService implements ActionButtonConfigService<Customer> {
  authService = inject(AuthService);
  router = inject(Router);
  selectedCustomerService = inject(SelectedCustomerService);
  ngbModal = inject(NgbModal);
  gungModalService = inject(GungModalService);
  navbarConfigService = inject(NavbarConfigService);
  reportCentralReportService = inject(ReportCentralReportService);
  featureActivatedGuardWrapper = inject(FeatureActivatedGuardWrapper);

  enableContacts = this.navbarConfigService.enableContacts;
  enableCustomerActivities = this.navbarConfigService.enableCustomerActivities;
  enableStatisticsExcelExport = this.navbarConfigService.enableStatisticsExcelExport;

  base = [
    {
      title: 'ADD_CONTACT',
      action: (item: Customer) => {
        this.gungModalService.openAddContactModal(item);
      }
    },
    {
      title: 'PLACE_ORDER',
      action: (item: Customer) => {
        this.selectedCustomerService.selectCustomer(item.id);
        this.authService
          .getCurrentUser()
          .pipe(first())
          .subscribe(user => {
            this.router.navigate(['products']);
          });
      }
    },
    {
      title: 'CREATE_USER',
      action: (item: Customer) => {
        this.createUser(item);
      }
    },
  ];


  statisticsExcelExport = [
    {
      title: "EXPORT_ORDER_STATISTICS",
      action: (item) => {
        this.reportCentralReportService.exportOrderStatistics(item.id).pipe(first()).subscribe(_ => {
          this.gungModalService.openConfirmYesNoModal('EXPORT_ORDER_STATISTICS', 'EXPORT_ORDER_STATISTICS_MODAL', {}, 'GO_TO_REPORT_CENTRAL', 'CLOSE').then(
            result => {
              if (result) {
                this.router.navigate(['/report-central']);
              }
            },
            reject => { }
          );
        });
      }
    },
    {
      title: "EXPORT_INVOICE_STATISITCS",
      action: (item) => {
        this.reportCentralReportService.exportInvoiceStatistics(item.id).pipe(first()).subscribe(_ => {
          this.gungModalService.openConfirmYesNoModal('EXPORT_INVOICE_STATISITCS', 'EXPORT_INVOICE_STATISITCS_MODAL', {}, 'GO_TO_REPORT_CENTRAL', 'CLOSE').then(
            result => {
              if (result) {
                this.router.navigate(['/report-central']);
              }
            },
            reject => { }
          );
        });
      }
    }
  ]


  extra = [
    {
      title: 'SELECT_CUSTOMER',
      action: (item: Customer) => {
        this.router.navigate(['customers', item.id]);
        this.selectedCustomerService.selectCustomer(item.id);
      }
    }
  ];

  activitiesActions = [
    {
      title: 'ADD_ACTIVITY',
      action: (item: Customer) => {
        this.createActivity(item);
      }
    }
  ];

  contactsActions = [
    {
      title: 'ADD_CONTACT',
      action: (item: Customer) => {
        this.gungModalService.openAddContactModal(item);
      }
    }
  ];

  constructor(
    @Optional()
    @Inject('environment')
    protected environment: NavigationConfig
  ) { }

  getButtons(item: Customer): Observable<ActionButton<Customer>[]> {
    return this.authService.getRoles().pipe(
      filter(roles => !!roles),
      first(),
      switchMap(roles => forkJoin({
        roles: of(roles),
        url: of(this.router.url),
        activitiesFeatureActivated: this.featureActivatedGuardWrapper.isActivated('gung-activities-internal', '3.0.13').pipe(first())
      })),
      switchMap(({ roles, url, activitiesFeatureActivated }) => {
        const isSalesOrAdmin: boolean = roles.indexOf('ADMIN') !== -1 || roles.indexOf('SALES') !== -1;
        const activitiesEnable = isSalesOrAdmin && activitiesFeatureActivated
        if (roles.indexOf('SALES') === -1) {
          return of([]);
        }

        if (this.environment.standardErp) {
          this.base = this.base.filter(a => a.title !== 'ADD_CONTACT');
        }

        if (this.router.url === '/customers') {
          return new Observable<ActionButton<Customer>[]>(subscriber => {
            subscriber.next([
              ...this.extra,
              ...this.base,
              ...(this.enableContacts ? this.contactsActions : []),
              ...(this.enableCustomerActivities || activitiesEnable ? this.activitiesActions : [])

            ]);
            subscriber.complete();
          });
        } else {
          return new Observable<ActionButton<Customer>[]>(subscriber => {
            subscriber.next([
              ...this.base,
              ...(this.enableContacts ? this.contactsActions : []),
              ...(this.enableCustomerActivities || activitiesEnable ? this.activitiesActions : []),
              ...(this.enableStatisticsExcelExport ? this.statisticsExcelExport : [])
            ]);
            subscriber.complete();
          });
        }
      })
    );
  }

  createUser(customer: Customer): void {
    const userToCreateFrom = {
      username: '',
      name: customer.name,
      email: '',
      password: '',
      managedCompanyIds: customer.id,
      roles: ['USER'],
      managedMultistockIds: [],
      managedAssortmentIds: [],
      assortment: '',
      activeGroups: {},
      phone: ''
    };

    const modalRef = this.ngbModal.open(CreateUserModalComponent, { size: 'lg' });
    modalRef.componentInstance.createUserFrom = userToCreateFrom;
    modalRef.result.then(
      (u: any) => {
        if (!!u) {
          this.router.navigate(['/admin/users-details/' + u.username]);
        }
      },
      reason => { }
    );
  }

  createActivity(customer: Customer): void {
    this.gungModalService.openCreateActivityModal(undefined, undefined, customer.id).then(
      result => { },
      reason => {
        console.log(`Dismissed ${reason}`);
      }
    );
  }
}
